import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Coins, SendHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  isFreeMessagesOfferActive,
  TEMPLATE_SEND_COST,
  type TemplateDto,
} from "@mandoob/shared";

import { useStartConversation } from "@/api/inbox";
import { useGetTemplates } from "@/api/templates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/language";
import { errorMessage } from "@/lib/errors";
import { hapticNotification } from "@/lib/haptics";
import { isNative } from "@/lib/capacitor";
import {
  formatPhoneNumberForDisplay,
  isDriverOwnNumber,
  normalizePhoneInput,
} from "@/lib/phone-utils";
import { cn, getErrorCode } from "@/lib/utils";
import { loadSession, useSession } from "@/session";

import {
  buildTemplateVariables,
  FIELD_LABEL_KEYS,
  fillTemplateBody,
  templateFields,
  templateNeeds,
  type FieldKey,
} from "./template-fields";

// Same storage as the new-delivery page, so both flows share the default.
const LAST_TEMPLATE_KEY = "mandoob:lastTemplateId";

function readLastTemplateId(): string | null {
  try {
    return localStorage.getItem(LAST_TEMPLATE_KEY);
  } catch {
    return null;
  }
}

/**
 * Quick send — the landing screen for WhatsApp links opened WITH the app
 * (wa.me / whatsapp:// → /chats/quick?phone=...). One screen: the number from
 * the link, a template to pick, only the fields that template actually uses,
 * a live preview, send. The full new-shipment page stays a tap away.
 */
export function QuickSendPage({ initialPhone }: { initialPhone?: string }) {
  const { t, dir } = useLanguage();
  const navigate = useNavigate();
  const session = useSession();
  const driver = session?.driver;

  const templatesQuery = useGetTemplates({ MaxResultCount: 100 });
  const templates = useMemo(
    () =>
      (templatesQuery.data?.items ?? []).filter(
        (tpl) => !tpl.hasMediaHeader && tpl.status === "Approved",
      ),
    [templatesQuery.data],
  );
  // No picker here: the driver's usual template (last used, else the first)
  // is applied silently — "edit shipment message" escapes to the full page.
  const [templateId] = useState<string | null>(readLastTemplateId);
  const template: TemplateDto | null =
    templates.find((tpl) => tpl.id === templateId) ?? templates[0] ?? null;
  const needs = template ? templateNeeds(template) : null;
  const usedFields = template ? templateFields(template) : [];

  const [clientPhone, setClientPhone] = useState(
    initialPhone ? `+${initialPhone}` : "",
  );
  const [phoneError, setPhoneError] = useState(false);
  const [clientName, setClientName] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  // Profile-backed fields arrive prefilled — visible only when the template
  // uses them, so the screen stays as short as the template allows.
  const [company, setCompany] = useState(driver?.company ?? "");
  const [driverName, setDriverName] = useState(driver?.name ?? "");
  const [driverPhone, setDriverPhone] = useState(
    driver?.callingPhone ?? driver?.phone ?? "",
  );
  // Templates outside the {ar|en}_field convention: one input per variable.
  const [genericValues, setGenericValues] = useState<Record<string, string>>(
    {},
  );

  const shipmentData = {
    clientName,
    trackingNumber,
    company,
    driverPhone: formatPhoneNumberForDisplay(driverPhone),
    driverName,
  };
  const fieldLabelFor = (field: FieldKey) => t(FIELD_LABEL_KEYS[field]);

  // Which of the template's fields were EMPTY when it resolved — those are
  // "the missing data" and get inputs; profile-backed values stay silent.
  // Snapshot per template so a field the driver clears doesn't vanish.
  const missingFields = useMemo(() => {
    if (!template || !needs?.mapped) return [] as FieldKey[];
    const values: Record<FieldKey, string> = {
      clientName,
      trackingNumber,
      company,
      driverName,
      driverPhone,
    };
    return templateFields(template).filter((field) => !values[field].trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id]);

  const preview = (() => {
    if (!template) return "";
    if (needs?.mapped) {
      return fillTemplateBody(template, shipmentData, fieldLabelFor);
    }
    let body = template.bodyText;
    for (const name of template.variables) {
      const value = genericValues[name]?.trim();
      if (value) body = body.replaceAll(`{{${name}}}`, value);
    }
    return body;
  })();

  const paymentsDisabled = !!session?.appPaymentsDisabled && isNative();
  // Free-messages offer: a new shipment's template is one of the free sends.
  const offerActive = isFreeMessagesOfferActive();
  const hasCredits =
    paymentsDisabled ||
    !driver ||
    offerActive ||
    (driver.creditsBalance ?? 0) >= TEMPLATE_SEND_COST;

  const startConversation = useStartConversation({
    mutation: {
      onSuccess: (conversation) => {
        hapticNotification("success");
        void loadSession().catch(() => undefined);
        void navigate({
          to: "/chats/$conversationId",
          params: { conversationId: conversation.id },
          replace: true,
        });
      },
      onError: (error) => {
        hapticNotification("error");
        const code = getErrorCode(error);
        if (code === "Waba:ClientBusyWithOtherDriver") {
          toast.error(t("Mandoob:NewDelivery.ClientBusy"));
          return;
        }
        if (code === "Waba:ClientHasActiveConversation") {
          toast.error(t("Mandoob:NewDelivery.ClientActive"));
          return;
        }
        if (code === "Credits:Insufficient") {
          toast.error(t("Mandoob:Credits.InsufficientToast"));
          return;
        }
        toast.error(errorMessage(error, t));
      },
    },
  });

  const canSend =
    !!template &&
    !!needs &&
    hasCredits &&
    clientPhone.trim().length > 0 &&
    (!usedFields.includes("trackingNumber") ||
      trackingNumber.trim().length > 0) &&
    (needs.mapped
      ? (!needs.company || company.trim().length > 0) &&
        (!needs.driverPhone || driverPhone.trim().length > 0) &&
        (!needs.driverName || driverName.trim().length > 0)
      : template.variables.every(
          (name) => (genericValues[name]?.trim() ?? "") !== "",
        ));

  const handleSend = () => {
    if (!template || !needs || startConversation.isPending) return;
    const phone = normalizePhoneInput(clientPhone);
    if (!phone) {
      setPhoneError(true);
      toast.error(t("Mandoob:Form.InvalidPhone"));
      return;
    }
    if (isDriverOwnNumber(driver, phone)) {
      setPhoneError(true);
      toast.error(t("Mandoob:NewDelivery.CannotMessageSelf"));
      return;
    }
    const normalizedDriverPhone = driverPhone.trim()
      ? normalizePhoneInput(driverPhone)
      : null;
    if (driverPhone.trim() && !normalizedDriverPhone) {
      toast.error(t("Mandoob:Form.InvalidPhone"));
      return;
    }

    let variables: Record<string, string>;
    if (needs.mapped) {
      variables = buildTemplateVariables(template, shipmentData)!;
    } else {
      variables = {};
      for (const name of template.variables)
        variables[name] = genericValues[name]?.trim() ?? "";
    }

    startConversation.mutate({
      clientPhone: phone,
      ...(clientName.trim() ? { clientName: clientName.trim() } : {}),
      ...(trackingNumber.trim()
        ? { trackingNumber: trackingNumber.trim() }
        : {}),
      ...(company.trim() ? { company: company.trim() } : {}),
      ...(driverName.trim() ? { driverName: driverName.trim() } : {}),
      ...(normalizedDriverPhone
        ? { driverCallingPhone: normalizedDriverPhone }
        : {}),
      // Landed here from a WhatsApp link opened with the app.
      source: "OpenWithApp",
      templateId: template.id,
      variables,
    });
  };

  const textField = (
    label: string,
    value: string,
    onChange: (next: string) => void,
    options?: {
      ltr?: boolean;
      error?: boolean;
      placeholder?: string;
      hint?: string;
    },
  ) => (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <Label>{label}</Label>
      <Input
        value={value}
        dir={options?.ltr ? "ltr" : undefined}
        onChange={(event) => onChange(event.target.value)}
        placeholder={options?.placeholder}
        aria-invalid={options?.error || undefined}
        className={cn(options?.error && "tw:border-destructive")}
      />
      {options?.hint ? (
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          {options.hint}
        </p>
      ) : null}
    </div>
  );

  return (
    <div dir={dir} className="tw:flex tw:h-full tw:flex-col">
      <header className="tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:border-b tw:border-border tw:px-3 tw:pb-3 tw:pt-[calc(12px+env(safe-area-inset-top))]">
        <button
          type="button"
          onClick={() => void navigate({ to: "/chats" })}
          aria-label={t("Mandoob:Back")}
          className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:transition-colors tw:hover:bg-muted"
        >
          <ArrowRight className="tw:size-5 tw:ltr:-scale-x-100" />
        </button>
        <div className="tw:flex tw:min-w-0 tw:flex-col">
          <h1 className="tw:m-0 tw:text-lg tw:font-semibold">
            {t("Mandoob:Quick.Title")}
          </h1>
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            {t("Mandoob:Quick.Subtitle")}
          </p>
        </div>
      </header>

      <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-4 tw:py-4">
        <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-xl tw:flex-col tw:gap-5 tw:pb-8">
          {/* The message FIRST — «labelled» placeholders show what is still
              missing; inputs for exactly those follow. */}
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <Label>{t("Mandoob:Quick.Preview")}</Label>
            {templatesQuery.isLoading ? (
              <Skeleton className="tw:h-24 tw:w-full tw:rounded-xl" />
            ) : !template ? (
              <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
                {t("Mandoob:NewDelivery.NoTemplates")}
              </p>
            ) : (
              <p className="tw:m-0 tw:whitespace-pre-wrap tw:rounded-xl tw:bg-muted tw:p-3 tw:text-sm tw:leading-relaxed tw:text-foreground">
                {preview}
              </p>
            )}
          </div>

          {/* The client number came from the WhatsApp link; an input appears
              only when the link had none (or the number failed validation). */}
          {(!initialPhone || phoneError) &&
            textField(
              t("Mandoob:NewDelivery.ClientPhone"),
              clientPhone,
              (next) => {
                setClientPhone(next);
                setPhoneError(false);
              },
              {
                ltr: true,
                error: phoneError,
                placeholder: "05xxxxxxxx",
                hint: t("Mandoob:NewDelivery.PhoneHint"),
              },
            )}

          {template && needs?.mapped ? (
            <>
              {missingFields.includes("clientName") &&
                textField(
                  t("Mandoob:NewDelivery.ClientName"),
                  clientName,
                  setClientName,
                  {
                    placeholder: t("Mandoob:NewDelivery.ClientNamePlaceholder"),
                    hint: t("Mandoob:NewDelivery.ClientNameHint"),
                  },
                )}
              {missingFields.includes("trackingNumber") &&
                textField(
                  t("Mandoob:Delivery.TrackingNumber"),
                  trackingNumber,
                  setTrackingNumber,
                  {
                    ltr: true,
                    placeholder: t("Mandoob:Delivery.TrackingPlaceholder"),
                  },
                )}
              {missingFields.includes("company") &&
                textField(t("Mandoob:Delivery.Company"), company, setCompany)}
              {missingFields.includes("driverName") &&
                textField(
                  t("Mandoob:Delivery.DriverName"),
                  driverName,
                  setDriverName,
                )}
              {missingFields.includes("driverPhone") &&
                textField(
                  t("Mandoob:Delivery.CallingPhone"),
                  driverPhone,
                  setDriverPhone,
                  {
                    ltr: true,
                    placeholder: "05xxxxxxxx",
                    hint: t("Mandoob:Delivery.CallingPhoneHint"),
                  },
                )}
            </>
          ) : null}
          {template && !needs?.mapped
            ? template.variables.map((name) =>
                textField(name, genericValues[name] ?? "", (next) =>
                  setGenericValues((prev) => ({ ...prev, [name]: next })),
                ),
              )
            : null}

          <div className="tw:flex tw:flex-col tw:items-stretch tw:gap-2">
            {!paymentsDisabled && (
              <span
                className={cn(
                  "tw:flex tw:items-center tw:justify-center tw:gap-1 tw:text-xs",
                  hasCredits
                    ? "tw:text-muted-foreground"
                    : "tw:text-destructive",
                )}
              >
                <Coins className="tw:size-3.5" strokeWidth={1.75} />
                {offerActive
                  ? t("Mandoob:Offer.FreeSend")
                  : hasCredits
                    ? t("Mandoob:Template.Cost", TEMPLATE_SEND_COST)
                    : t("Mandoob:Credits.InsufficientToast")}
              </span>
            )}
            <Button
              type="button"
              size="lg"
              onClick={handleSend}
              disabled={!canSend}
              loading={startConversation.isPending}
              className="tw:gap-2"
            >
              {t("Mandoob:Quick.Send")}
              <SendHorizontal className="tw:size-5 tw:rtl:-scale-x-100" />
            </Button>
            <button
              type="button"
              onClick={() =>
                void navigate({
                  to: "/chats/new",
                  search: initialPhone ? { phone: initialPhone } : {},
                })
              }
              className="tw:self-center tw:text-xs tw:text-muted-foreground tw:underline-offset-2 tw:hover:underline"
            >
              {t("Mandoob:Quick.EditMessage")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
